import React, {Component} from "react"
import { Redirect, Link } from "react-router-dom";
import { TileLayer, Polyline } from 'react-leaflet'
import * as L from "leaflet";
import GeoUtil from "leaflet-geometryutil";
import ContainerDimensions from 'react-container-dimensions'
import MapContainer from './MapContainer';
import TruckMarker from "./TruckMarker";
import JsxMarker from "./JsxMarker";
import COMPANIES from "./companies";

const apiIsLoaded = (map, setMap) => {
	setMap(map);
};

const fps = 60;
const mspr = 12000;
const timeBetweenMapdata = 6000;

const center = [51.1726744, 4.1326231];
const radiusInKm = 200;

const markerALatLng = L.marker(center).getLatLng();

class TrackerView extends Component {
	constructor() {
		super();

		const url = new URL(window.location.href);
		const apikey = url.searchParams.get("apikey");

		this.state = {
			map: null,

			angle: 0,
			polyline: [],
			locations: [],

			apikey: apikey ?? null,
		};

		this.truckLocations = [];

		this.setMap = this.setMap.bind(this);
		this.moveMap = this.moveMap.bind(this);		
		this.getMapdata = this.getMapdata.bind(this);		
		this.frameRender = this.frameRender.bind(this);		
	}
	
	componentDidMount() {
		const timeBetweenFrames = 1000/ fps;
		this.frameRenderInterval = setInterval(this.frameRender, timeBetweenFrames);
		this.getMapdataInterval = setInterval(this.getMapdata, timeBetweenMapdata);
		this.getMapdata();
	}

	componentWillUnmount() {
		if (this.frameRenderInterval) clearTimeout(this.frameRenderInterval);
		if (this.getMapdataInterval) clearTimeout(this.getMapdataInterval);
	}

	static defaultProps = {
		center: {
			lat: process.env.REACT_APP_DEFAULT_LAT,
			lng: process.env.REACT_APP_DEFAULT_LNG,
		},
		zoom: 10,
	};

	moveMap(lat, long) {
		if (lat != this.state.lastFocusLatitude || long != this.state.lastFocusLongitude) {
			this.setState({
				lastFocusLatitude: lat,
				lastFocusLongitude: long,
			});
			if (this.state.map != null && !(lat == 0 && long == 0)) {
				this.state.map.panTo({
					lat: parseFloat(lat),
					lng: parseFloat(long)
				});
			}
		}
	}

	setMap(map) {
		if (!this.state.map) {
			this.setState({
				map: map
			});
		}
	}

	getMapdata() {
		const that = this;
		const base = process.env.REACT_APP_ICECORP_API_HREF;

		// De publieke IceCorp-endpoint werkt per klant (company_id) zonder apikey,
		// net zoals de individuele klant-trackers. We bevragen alle klanten uit
		// COMPANIES parallel en voegen de resultaten samen tot één radar-beeld.
		Promise.all(COMPANIES.map(company => {
			const url = base+"v1/icecreamvanmarkerdata?company_id="+company.id+"&has_working_day=1";
			return fetch(url)
				.then(response => response.ok ? response.json() : { data: [] })
				.then(json => ({ company, data: (json && Array.isArray(json.data)) ? json.data : [] }))
				.catch(() => ({ company, data: [] }));
		}))
		.then(results => {
			const merged = [];
			results.forEach(({ company, data }) => {
				data.forEach(truckLocation => {
					if (!(truckLocation.latitude > 0 && truckLocation.longitude > 0)) return;

					const markerC = L.marker([truckLocation.latitude, truckLocation.longitude]);
					let newMarkerRelativeAngle = GeoUtil.bearing(markerALatLng, markerC.getLatLng());
					if (newMarkerRelativeAngle < 0) {
						newMarkerRelativeAngle = 180+(180-Math.abs(newMarkerRelativeAngle));
					}

					merged.push({
						id: truckLocation.id,
						// Kleur + klantnaam komen van de klant die we bevragen; als de API
						// later company_name/nr meestuurt, krijgen die voorrang.
						companyId: truckLocation.company_id || company.id,
						clientName: truckLocation.company_name || company.name,
						nr: truckLocation.nr,
						title: truckLocation.title,
						lat: truckLocation.latitude,
						lng: truckLocation.longitude,
						angle: newMarkerRelativeAngle,
					});
				});
			});
			that.truckLocations = merged;
		})
		.catch((e) => {
			console.error(e);
		});
}

	frameRender() {
		const oldAngle = this.state.angle;
		const newAngle = this.state.angle + ( ((1000/ fps) / mspr)*360 );
		const startOfCircleAngle = Math.floor(newAngle/360)*360;
		const relativeOldAngel = oldAngle%360;
		const relativeNewAngel = newAngle%360;

		const pointB = L.point(GeoUtil.destination(markerALatLng, oldAngle, radiusInKm * 1000));

		const polyline = [
			[markerALatLng.lat, markerALatLng.lng],
			[pointB.x.lat, pointB.x.lng],
		];

		let locations = this.state.locations.filter(location => location.angle >= newAngle-360 );

		const truckLocations = this.truckLocations.filter(truckLocation => truckLocation.angle >= relativeOldAngel && truckLocation.angle <= relativeNewAngel);
		truckLocations.forEach(truckLocation => {
			const markerC = L.marker([truckLocation.lat, truckLocation.lng]);
			let newMarkerRelativeAngle = GeoUtil.bearing(markerALatLng, markerC.getLatLng());
			if (newMarkerRelativeAngle < 0) {
				newMarkerRelativeAngle = 180+(180-Math.abs(newMarkerRelativeAngle));
			}

			locations.push({
				key: truckLocation.id+"-"+newAngle,
				companyId: truckLocation.companyId,
				clientName: truckLocation.clientName,
				nr: truckLocation.nr,
				title: truckLocation.title,
				lat: markerC.getLatLng().lat,
				lng: markerC.getLatLng().lng,
				angle: startOfCircleAngle+newMarkerRelativeAngle,
			});
		});

		this.setState({
			angle: newAngle,
			polyline: polyline,
			locations: locations,
		});
	}
	
	render() {
			return (
				<main>
					<div className={"content contentNoHeader"}>
						{/* Important! Always set the container height explicitly */}
						<div className={"mapdivNoHeader"}>
							<ContainerDimensions>
								<MapContainer center={this.props.center} zoom={this.props.zoom} ref='map'>
									<TileLayer
										attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
										url={process.env.REACT_APP_TILE_SERVER_HREF+"?z={z}&x={x}&y={y}"}
										onLoad={(props) => apiIsLoaded(props.target._map, this.setMap)}
									/>
									<Polyline color={"#03A062"} positions={this.state.polyline} />
									{this.state.locations.map(location => <TruckMarker key={location.key} markerKey={location.key} lat={location.lat} lng={location.lng} companyId={location.companyId} clientName={location.clientName} nr={location.nr} title={location.title} />)}
								</MapContainer>
							</ContainerDimensions>
						</div>
					</div>
				</main>
			)
	}
}

export default TrackerView;